// useLaneEditHistory.js — Lightweight undo/redo for lane waypoint editing.

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks waypoint snapshots during lane edit mode for undo/redo support.
 *
 * Usage:
 *   const history = useLaneEditHistory(laneEditMode);
 *   // On entering edit mode:  history.initialize(lane.waypoints)
 *   // Before each mutation:   history.pushState(lane.waypoints)
 *   // Undo:  const prev = history.undo();   if (prev) applyWaypoints(prev);
 *   // Redo:  const next = history.redo();   if (next) applyWaypoints(next);
 */
export function useLaneEditHistory(enabled) {
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const currentRef = useRef(null);

  const initialize = useCallback((waypoints) => {
    setPast([]);
    setFuture([]);
    currentRef.current = JSON.parse(JSON.stringify(waypoints));
  }, []);

  const pushState = useCallback((waypoints) => {
    if (currentRef.current) {
      setPast((prev) => [...prev, currentRef.current]);
    }
    setFuture([]);
    currentRef.current = JSON.parse(JSON.stringify(waypoints));
  }, []);

  const canUndo = past.length > 0;
  const canRedo = future.length > 0;

  const undo = useCallback(() => {
    if (!past.length) return null;
    const prev = past[past.length - 1];
    setPast((h) => h.slice(0, -1));
    setFuture((f) => [...f, currentRef.current]);
    currentRef.current = prev;
    return prev;
  }, [past]);

  const redo = useCallback(() => {
    if (!future.length) return null;
    const next = future[future.length - 1];
    setFuture((f) => f.slice(0, -1));
    setPast((h) => [...h, currentRef.current]);
    currentRef.current = next;
    return next;
  }, [future]);

  // Reset when edit mode is disabled
  useEffect(() => {
    if (!enabled) {
      setPast([]);
      setFuture([]);
      currentRef.current = null;
    }
  }, [enabled]);

  return { initialize, pushState, undo, redo, canUndo, canRedo };
}
